import { useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Divider, Stack, Typography } from '@mui/material';

import routes from 'constants/routes';
import Button from 'components/Common/Button';
import Tab from 'components/Common/Tab';
import ActivityContainer from 'components/Common/ActivityContainerTable';
import ParishonerProfileContainer from 'components/Community/ParishonerProfileContainer';
import MenuList from 'components/Common/MenuList';
import Modal from 'components/Common/Modal';
import { RootState, store } from 'redux/store';
import { fetchAnnouncementGroupDetail, removeMemberFromGroup } from 'redux/slices/announcements/announcementGroupDetailSlice';
import client from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import ParishUsersAutocomplete from 'components/Common/autocomplete/ParishUsersAutocomplete';
import { get } from 'utils/helpers';
import { duplicateGroupById } from 'redux/slices/announcements/announcementGroupSlice';
import { setAnnouncementsTab } from 'redux/slices/common/appSlice';
import { ANNOUNCEMENT_GROUPS } from 'types/announcements';

const columns = [
    { id: 'name', label: 'Name', minWidth: '200px', path: 'userResponse.name' },
    { id: 'username', label: 'Username', path: 'userResponse.username' },
    { id: 'parishionerId', label: 'Parishioner ID#' },
    { id: 'status', label: 'Status' },
];
const textInputPlaceholders = ['Total Members:', 'Total Pending:', 'Search Here'];

const USERS_LOADING = 'usersLoading';
const ADD_MEMBER_LOADING = 'addMemberLoading';
const REMOVE_MEMBERS_LOADING = 'removeMembersLoading';
const DUPLICATE_GROUP_LOADING = 'duplicateGroupLoading';

type LoadingActionType = 'usersLoading' | 'addMemberLoading' | 'removeMembersLoading' | 'duplicateGroupLoading';
interface LoadingAction {
    type: LoadingActionType;
}

const initialLoadingState = {
    [USERS_LOADING]: false,
    [ADD_MEMBER_LOADING]: false,
    [REMOVE_MEMBERS_LOADING]: false,
    [DUPLICATE_GROUP_LOADING]: false,
};

const loadingReducer = (state: typeof initialLoadingState, action: LoadingAction) => {
    return { ...state, [action.type]: !state[action.type] };
};

function AnnouncementGroupDetails() {
    const dispatch = useDispatch<typeof store.dispatch>();
    const navigate = useNavigate();
    const [tab, setTab] = useState('');
    const [loading, setLoading] = useReducer(loadingReducer, initialLoadingState);
    const [addNewMemberModal, setAddNewMemberModal] = useState(false);
    const [removeMemberModal, setRemoveMemberModal] = useState(false);
    const [addMember, setAddMember] = useState<any>(null);
    const [removeMember, setRemoveMember] = useState<any>(null);
    const { details, members, membersLoading, detailsLoading } = useSelector(
        (state: RootState) => state.announcement.announcementGroupDetail
    );
    const { id } = useParams();
    const { title, imageUrl, totalParticipants } = details || {};

    const membersIds = members.map((member) => member?.userResponse?.id);

    useEffect(() => {
        if (id) {
            dispatch(fetchAnnouncementGroupDetail(id));
        }
    }, [id, dispatch]);

    const setTabValue = (value: string) => {
        setTab(value);
    };

    const navigationHandler = (row: any) => {
        const userName = get(row, 'userResponse.username', '');
        if (userName) {
            navigate(routes.user.userDetail.replace(':username', userName));
        }
    };

    const handleAddMemberClick = () => {
        setAddNewMemberModal(true);
    };

    const handleLoading = (type: LoadingActionType) => setLoading({ type });

    const handleAddMember = async () => {
        if (!addMember) return;
        const payload = {
            announcementGroup: { id },
            userName: addMember?.username,
        };
        try {
            handleLoading(ADD_MEMBER_LOADING);
            const res: any = await client.post('/createAnnouncementGroupUser', payload);
            if (res) {
                notify('Member added successfully.', 'success');
                setAddNewMemberModal(false);
                setAddMember(null);
                id && dispatch(fetchAnnouncementGroupDetail(id));
            }
        } catch (e) {
        } finally {
            handleLoading(ADD_MEMBER_LOADING);
        }
    };

    const handleRemoveMemberClick = (item: object) => {
        setRemoveMember(item);
        setRemoveMemberModal(true);
    };

    const onConfirm = async () => {
        if (!id) return;
        handleLoading(REMOVE_MEMBERS_LOADING);
        const { payload } = await dispatch(
            removeMemberFromGroup({ userName: removeMember?.userResponse?.username, announcementGroupId: Number(id) })
        );
        handleLoading(REMOVE_MEMBERS_LOADING);
        if (payload) {
            setRemoveMemberModal(false);
        }
    };

    const handleSendAnnouncement = () => {
        if (!details) return;
        navigate(routes.announcements.new, { state: { group: details } });
    };
    const handleDuplicateGroup = async () => {
        if (!id) return;
        handleLoading(DUPLICATE_GROUP_LOADING);
        const { payload }: any = await dispatch(duplicateGroupById(id));
        handleLoading(DUPLICATE_GROUP_LOADING);
        if (payload?.code === 200) {
            setAnnouncementsTab(ANNOUNCEMENT_GROUPS);
            navigate(routes.announcements.main);
        }
    };
    return (
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent={'space-evenly'}>
            <div>
                <Container>
                    <ParishonerProfileContainer loading={detailsLoading} title={title} imgUrl={imageUrl} subtitle="" showIcon={false} />
                </Container>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, marginBottom: 20 }}>
                    <Button disabled={detailsLoading} onClick={handleSendAnnouncement} width={'23rem'} title={'Send Announcement'} />
                </div>
            </div>
            <Stack sx={{ backgroundColor: 'white', p: 1, mx: 2, width: '100%', overflow: 'auto' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-around',
                    }}
                >
                    <Tab tabs={[{ value: 'details', label: 'Details' }]} tabValue={tab} setTabValue={setTabValue} />
                    <MenuList
                        mt={2}
                        menuItems={[
                            { item: 'Add Member', onClick: handleAddMemberClick },
                            // { item: 'Remove Member', onClick: () => setRemoveMemberModal(true) },
                            {
                                item: 'Duplicate Group',
                                onClick: handleDuplicateGroup,
                                loading: loading[DUPLICATE_GROUP_LOADING],
                                loaderProps: {
                                    sx: {
                                        minWidth: 149,
                                    },
                                },
                            },
                        ]}
                    />
                </div>
                <Divider light={true} />

                {/* Activity Container - Contains Table which holds data */}
                <ActivityContainer
                    showDeleteCell={true}
                    navigationHandler={navigationHandler}
                    records={members}
                    columns={columns}
                    textInputPlaceholders={textInputPlaceholders}
                    onDeleteClick={handleRemoveMemberClick}
                    value={totalParticipants ?? ''}
                    getStartedTemplate
                    onTemplatebuttonclick={handleAddMemberClick}
                    templateButtonText="Add New Member"
                    loading={membersLoading}
                    totalCount={members.length}
                    showLoading={!!loading}
                />
                {/* <Container
                    component={() => (
                    )}
                /> */}
            </Stack>

            {/* {Add New Member Modal} */}
            <Modal
                isOpen={addNewMemberModal}
                modalTitle="Add new member"
                onClose={() => {
                    setAddNewMemberModal(false);
                    setAddMember(null);
                }}
            >
                <>
                    <Typography sx={{ fontSize: 18, my: 2, fontWeight: 400, textAlign: 'center' }}>
                        Type the parishioners name, nickname, or parishioner ID# to find their user account. Then click “Add Member” to add
                        them to this group. <br />
                    </Typography>
                    <ParishUsersAutocomplete excludeUsers={membersIds} user={addMember} setUser={setAddMember} />
                    <Typography sx={{ textAlign: 'center', fontSize: 18, fontWeight: 40, my: 4 }}>
                        User accounts to be added: <br />
                        Name/username
                    </Typography>
                    <Button
                        loading={loading[ADD_MEMBER_LOADING]}
                        disabled={loading[USERS_LOADING]}
                        onClick={handleAddMember}
                        width={'100%'}
                        title="Add Member"
                    />
                </>
            </Modal>

            {/* {Remove Member Modal} */}
            <Modal onClose={() => setRemoveMemberModal(false)} isOpen={removeMemberModal} modalTitle="Confirm Remove">
                <>
                    <Typography sx={{ fontSize: 18, my: 2, color: '#1F2937', fontWeight: 400 }}>
                        Are you sure you want to remove
                        <br />
                        <span style={{ fontWeight: 550, fontSize: 20, marginTop: 4, marginBottom: 4 }}>
                            {removeMember?.userResponse?.name}
                        </span>
                        <br />
                        From this group?
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button backgroundColor="white" onClick={() => setRemoveMemberModal(false)} width="45%" title="Cancel" />
                        <Button loading={loading[REMOVE_MEMBERS_LOADING]} onClick={onConfirm} width="45%" title="Confirm" />
                    </div>
                </>
            </Modal>
        </Stack>
    );
}

export default AnnouncementGroupDetails;
